class ProductAvailabilityObserver extends HTMLElement {
  constructor() {
    super();

    this.currentCollectionHandle = this.dataset.collectionHandle || '';
    this.isLastPage = this.hasAttribute('data-last-page');

    this.observer = null;
    this.gridObserver = null;
    this.infiniteObserver = null;

    this._onProductsAppended = this._onProductsAppended.bind(this);
    this._onInfiniteScrollEnd = this._onInfiniteScrollEnd.bind(this);
    this._debug = this.hasAttribute('data-debug');
  }

  connectedCallback() {
    this.init();
    document.addEventListener('collection:products-appended', this._onProductsAppended);
    document.addEventListener('collection:infinite-scroll-end', this._onInfiniteScrollEnd);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.gridObserver) this.gridObserver.disconnect();
    if (this.infiniteObserver) this.infiniteObserver.disconnect();

    document.removeEventListener('collection:products-appended', this._onProductsAppended);
    document.removeEventListener('collection:infinite-scroll-end', this._onInfiniteScrollEnd);
  }

  init() {
    this.createObserver();
    this.observeProducts();
    this.observeGridInsertions();
    this.watchInfiniteScrollEnd();

    // Safety re-scan (covers late DOM injection / filter re-render)
    setTimeout(() => this.observeProducts(), 0);
  }

  log(...args) {
    if (!this._debug) return;
    // eslint-disable-next-line no-console
    console.log(...args);
  }

  getStorageKey() {
    const handle = this.currentCollectionHandle || window.location.pathname || 'collection';
    return `unavailableProducts_${handle}`;
  }

  getStoredProducts() {
    const stored = localStorage.getItem(this.getStorageKey());
    try {
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  }

  setStoredProducts(products) {
    localStorage.setItem(this.getStorageKey(), JSON.stringify(products || []));
  }

  isAvailable(productElement) {
    if (!productElement || productElement.nodeType !== 1) return true;

    const ds = productElement.dataset?.productAvailable;
    if (ds === 'true') return true;
    if (ds === 'false') return false;

    const attr = productElement.getAttribute?.('data-product-available');
    if (attr === 'true') return true;
    if (attr === 'false') return false;

    // Fallback: sold-out tag present in markup
    if (productElement.querySelector?.('.grid-product__tag--high-priority.sold--out')) return false;
    return true;
  }

  storeProduct(productId, productElement) {
    if (!productId || !productElement) return;

    const storedProducts = this.getStoredProducts();
    if (storedProducts.some(p => String(p.id) === String(productId))) return;

    // Store clean HTML (avoid persisting observer flags)
    const clone = productElement.cloneNode(true);
    clone.removeAttribute('data-product-availability-observed');
    clone.removeAttribute('data-restored-unavailable');

    storedProducts.push({
      id: productId,
      handle: productElement.dataset.productHandle,
      html: clone.outerHTML
    });

    this.setStoredProducts(storedProducts);
    this.log('Stored unavailable product:', productId, 'for collection:', this.currentCollectionHandle);
  }

  removeFromStorage(productId) {
    if (!productId) return;
    const storedProducts = this.getStoredProducts().filter(p => String(p.id) !== String(productId));
    this.setStoredProducts(storedProducts);
  }

  handleAvailabilityChange(productElement) {
    if (!productElement) return;
    if (productElement.dataset.restoredUnavailable === 'true') return;

    const productId = productElement.dataset.productId;
    const isAvailable = this.isAvailable(productElement);

    if (!isAvailable) {
      this.storeProduct(productId, productElement);
      productElement.remove();
    } else {
      this.removeFromStorage(productId);
    }
  }

  addStoredProductsBack() {
    if (!this.isLastPage) return;

    const storedProducts = this.getStoredProducts();
    if (!storedProducts.length) return;

    const grid = this.getGridContainer() || this;

    storedProducts.forEach((productData) => {
      try {
        const id = productData?.id;
        if (!id) return;

        // Avoid duplicates if user navigates back/forward.
        if (grid.querySelector?.(`[data-product-id="${CSS.escape(String(id))}"]`)) return;

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = productData.html;
        const productElement = tempDiv.firstElementChild;
        if (!productElement) return;

        productElement.dataset.restoredUnavailable = 'true';
        grid.appendChild(productElement);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error adding back product:', productData?.id, e);
      }
    });

    localStorage.removeItem(this.getStorageKey());
  }

  createObserver() {
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-product-available') {
          this.handleAvailabilityChange(mutation.target);
        }
      });
    });
  }

  observeProducts(roots = null) {
    if (!this.observer) return;

    const items = this.normalizeProductItems(roots);
    items.forEach((item) => {
      if (!item || item.nodeType !== 1) return;
      if (item.dataset.restoredUnavailable === 'true') return;
      if (item.dataset.productAvailabilityObserved === 'true') return;

      // Remove/store sold-out immediately so it never sits mid-grid.
      if (!this.isAvailable(item)) {
        this.handleAvailabilityChange(item);
        return;
      }

      item.dataset.productAvailabilityObserved = 'true';

      this.observer.observe(item, {
        attributes: true,
        attributeFilter: ['data-product-available']
      });
    });
  }

  normalizeProductItems(roots = null) {
    if (!roots) {
      return Array.from(this.querySelectorAll('.grid__item.product__grid-item'));
    }

    const rootList = Array.isArray(roots) ? roots : Array.from(roots);
    const items = [];

    rootList.forEach((root) => {
      if (!root || root.nodeType !== 1) return;
      if (root.matches?.('.grid__item.product__grid-item')) {
        items.push(root);
        return;
      }
      const found = root.querySelectorAll?.('.grid__item.product__grid-item');
      if (found && found.length) items.push(...Array.from(found));
    });

    return items;
  }

  getGridContainer() {
    // In this theme, the custom element itself is the grid container.
    if (this.querySelector?.('.grid__item.product__grid-item')) return this;
    return this.matches?.('[data-product-grid]') ? this : (this.querySelector?.('[data-product-grid]') || null);
  }

  observeGridInsertions() {
    const grid = this.getGridContainer() || this;
    if (this.gridObserver) this.gridObserver.disconnect();

    this.gridObserver = new MutationObserver((mutations) => {
      const added = [];
      mutations.forEach((m) => {
        if (m.type !== 'childList' || !m.addedNodes?.length) return;
        added.push(...Array.from(m.addedNodes));
      });
      if (added.length) this.observeProducts(added);
    });

    this.gridObserver.observe(grid, { childList: true, subtree: true });
  }

  getInfiniteScrollEl() {
    const section = this.closest?.('.shopify-section') || document;
    return section.querySelector?.('infinite-scroll') || null;
  }

  watchInfiniteScrollEnd() {
    // If we are already server-rendered as last page, restore.
    if (this.isLastPage) {
      this.addStoredProductsBack();
      return;
    }

    const infinite = this.getInfiniteScrollEl();
    if (!infinite) return;

    const isEnd = () => {
      const href = infinite.querySelector?.('a')?.getAttribute?.('href');
      return !href;
    };

    if (isEnd()) {
      this._onInfiniteScrollEnd();
      return;
    }

    if (this.infiniteObserver) this.infiniteObserver.disconnect();
    this.infiniteObserver = new MutationObserver(() => {
      if (isEnd()) this._onInfiniteScrollEnd();
    });
    this.infiniteObserver.observe(infinite, { childList: true, subtree: true, attributes: true });
  }

  _onProductsAppended(evt) {
    const cards = evt?.detail?.cards;
    if (!cards || !cards.length) return;
    this.observeProducts(cards);
  }

  _onInfiniteScrollEnd() {
    this.isLastPage = true;
    this.addStoredProductsBack();
  }
}

customElements.define('product-availability-observer', ProductAvailabilityObserver);